import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

/**
 * Lets a page replace the breadcrumb title its route loader produced.
 *
 * Loader crumbs are resolved once per navigation, so a page editing the thing
 * the crumb names (a workflow's name in the editor, say) otherwise shows a
 * stale label — or a placeholder like "New Workflow" — until the next load.
 * Overrides are keyed by route id so a page can relabel an *ancestor* crumb,
 * not just its own.
 */
interface BreadcrumbOverrideContextValue {
  overrides: Readonly<Record<string, string>>;
  setOverride: (routeId: string, title: string | null) => void;
}

const BreadcrumbOverrideContext =
  createContext<BreadcrumbOverrideContextValue | null>(null);

export function BreadcrumbOverrideProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [overrides, setOverrides] = useState<Record<string, string>>({});

  const setOverride = useCallback((routeId: string, title: string | null) => {
    setOverrides((prev) => {
      if (title === null) {
        if (!(routeId in prev)) return prev;
        const next = { ...prev };
        delete next[routeId];
        return next;
      }
      if (prev[routeId] === title) return prev;
      return { ...prev, [routeId]: title };
    });
  }, []);

  const value = useMemo(
    () => ({ overrides, setOverride }),
    [overrides, setOverride],
  );

  return (
    <BreadcrumbOverrideContext.Provider value={value}>
      {children}
    </BreadcrumbOverrideContext.Provider>
  );
}

/** Read the current overrides — for the component that renders the crumbs. */
export function useBreadcrumbOverrides(): Readonly<Record<string, string>> {
  return useContext(BreadcrumbOverrideContext)?.overrides ?? {};
}

/**
 * Publish a breadcrumb title for `routeId` for as long as the caller is
 * mounted. Pass `null` (or an empty title) to fall back to the loader crumb.
 */
export function useBreadcrumbTitleOverride(
  routeId: string | undefined,
  title: string | null,
): void {
  const setOverride = useContext(BreadcrumbOverrideContext)?.setOverride;
  const resolved = title?.trim() ? title.trim() : null;

  useEffect(() => {
    if (!setOverride || !routeId) return;
    setOverride(routeId, resolved);
    return () => setOverride(routeId, null);
  }, [setOverride, routeId, resolved]);
}
